import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import {
  companyInfo,
  services,
  team,
  partnerships,
  projects,
  researchStats,
  coreValues,
  contactInfo
} from '../data/mock';
import {
  Mountain,
  Leaf,
  Factory,
  Beaker,
  Users,
  Atom,
  FlaskConical,
  TrendingUp,
  Search,
  ShieldCheck,
  Award,
  GraduationCap,
  Phone,
  Mail,
  MapPin,
  Globe,
  Hammer,
  Building2,
  Target,
  Eye,
  Heart,
  TestTubes
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { Badge } from '../components/ui/badge';

const iconMap = {
  flask: TestTubes,
  mountain: Mountain,
  leaf: Leaf,
  factory: Factory,
  beaker: Beaker,
  users: Users,
  atom: Atom,
  pickaxe: Hammer,
  'flask-conical': FlaskConical,
  'trending-up': TrendingUp,
  search: Search,
  'shield-check': ShieldCheck,
  award: Award,
  'graduation-cap': GraduationCap
};

const Home = () => {
  const { language, toggleLanguage, t } = useLanguage();
  const [activeSection, setActiveSection] = useState('home');

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setActiveSection(sectionId);
    }
  };

  const IconComponent = ({ name, className }) => {
    const Icon = iconMap[name] || Building2;
    return <Icon className={className} />;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-slate-900/95 backdrop-blur-sm border-b border-slate-700 shadow-lg">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <div className="flex items-center space-x-3 cursor-pointer" onClick={() => scrollToSection('home')}>
              <div className="w-12 h-12 bg-amber-500 rounded flex items-center justify-center">
                <Building2 className="w-7 h-7 text-slate-900" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">{companyInfo.name}</h1>
                <p className="text-xs text-amber-400">{t(companyInfo.tagline)}</p>
              </div>
            </div>

            {/* Navigation - Always visible */}
            <nav className="flex items-center space-x-2">
              <Button
                variant="ghost"
                className="text-slate-300 hover:text-white hover:bg-slate-800 font-medium"
                onClick={() => scrollToSection('home')}
              >
                {language === 'en' ? 'Home' : 'Accueil'}
              </Button>
              <Button
                variant="ghost"
                className="text-slate-300 hover:text-white hover:bg-slate-800 font-medium"
                onClick={() => scrollToSection('services')}
              >
                {language === 'en' ? 'Services' : 'Services'}
              </Button>
              <Button
                variant="ghost"
                className="text-slate-300 hover:text-white hover:bg-slate-800 font-medium"
                onClick={() => scrollToSection('about')}
              >
                {language === 'en' ? 'About' : 'À Propos'}
              </Button>
              <Button
                variant="ghost"
                className="text-slate-300 hover:text-white hover:bg-slate-800 font-medium"
                onClick={() => scrollToSection('projects')}
              >
                {language === 'en' ? 'Projects' : 'Projets'}
              </Button>
              <Button
                variant="ghost"
                className="text-slate-300 hover:text-white hover:bg-slate-800 font-medium"
                onClick={() => scrollToSection('contact')}
              >
                {language === 'en' ? 'Contact' : 'Contact'}
              </Button>
              
              {/* Language Toggle */}
              <Button
                onClick={toggleLanguage}
                variant="outline"
                size="sm"
                className="ml-4 border-amber-500 text-amber-500 hover:bg-amber-500 hover:text-slate-900 font-semibold"
              >
                <Globe className="w-4 h-4 mr-2" />
                {language === 'en' ? 'FR' : 'EN'}
              </Button>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section id="home" className="pt-24 pb-16 bg-slate-900 text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center py-16">
            <Badge className="mb-6 bg-amber-500 text-slate-900 hover:bg-amber-600">
              {t(companyInfo.tagline)}
            </Badge>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              {language === 'en'
                ? 'Leading Industrial & Mining Solutions in R.D. Congo'
                : 'Solutions Industrielles et Minières de Premier Plan en R.D. Congo'}
            </h2>
            <p className="text-xl text-slate-300 mb-8 leading-relaxed">
              {t(companyInfo.mission)}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button
                size="lg"
                className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold"
                onClick={() => scrollToSection('services')}
              >
                {language === 'en' ? 'Explore Services' : 'Découvrir nos Services'}
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-slate-600 text-white hover:bg-slate-800"
                onClick={() => scrollToSection('contact')}
              >
                {language === 'en' ? 'Get in Touch' : 'Nous Contacter'}
              </Button>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto mt-16">
            <div className="bg-slate-800 p-6 rounded-lg border border-slate-700">
              <div className="text-3xl font-bold text-amber-500 mb-2">{researchStats.projects.number}</div>
              <div className="text-sm text-slate-300">{t(researchStats.projects.label)}</div>
            </div>
            <div className="bg-slate-800 p-6 rounded-lg border border-slate-700">
              <div className="text-3xl font-bold text-amber-500 mb-2">4</div>
              <div className="text-sm text-slate-300">
                {language === 'en' ? 'Expert Teams' : 'Équipes Expertes'}
              </div>
            </div>
            <div className="bg-slate-800 p-6 rounded-lg border border-slate-700">
              <div className="text-3xl font-bold text-amber-500 mb-2">2021</div>
              <div className="text-sm text-slate-300">
                {language === 'en' ? 'Established Partnerships' : 'Partenariats Établis'}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              {language === 'en' ? 'Our Services' : 'Nos Services'}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {language === 'en'
                ? 'Comprehensive industrial and mining solutions tailored to your needs'
                : 'Solutions industrielles et minières complètes adaptées à vos besoins'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service) => (
              <Card
                key={service.id}
                className="border-slate-200 hover:border-amber-500 hover:shadow-lg transition-all duration-300"
              >
                <CardHeader>
                  <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center mb-4">
                    <IconComponent name={service.icon} className="w-6 h-6 text-amber-600" />
                  </div>
                  <CardTitle className="text-slate-900">{t(service.title)}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-slate-600">{t(service.description)}</CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              {language === 'en' ? 'About Us' : 'À Propos de Nous'}
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
            <Card className="bg-white border-slate-200">
              <CardHeader>
                <Target className="w-10 h-10 text-amber-600 mb-4" />
                <CardTitle className="text-slate-900">
                  {language === 'en' ? 'Our Goal' : 'Notre Objectif'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600">{t(companyInfo.goal)}</p>
              </CardContent>
            </Card>

            <Card className="bg-white border-slate-200">
              <CardHeader>
                <Heart className="w-10 h-10 text-amber-600 mb-4" />
                <CardTitle className="text-slate-900">
                  {language === 'en' ? 'Our Mission' : 'Notre Mission'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600">{t(companyInfo.mission)}</p>
              </CardContent>
            </Card>

            <Card className="bg-white border-slate-200">
              <CardHeader>
                <Eye className="w-10 h-10 text-amber-600 mb-4" />
                <CardTitle className="text-slate-900">
                  {language === 'en' ? 'Our Vision' : 'Notre Vision'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600">{t(companyInfo.vision)}</p>
              </CardContent>
            </Card>
          </div>

          {/* Core Values */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-slate-900 mb-8 text-center">
              {language === 'en' ? 'Core Values' : 'Valeurs Fondamentales'}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {coreValues.map((value) => (
                <div key={value.id} className="bg-white p-6 rounded-lg border border-slate-200">
                  <IconComponent name={value.icon} className="w-8 h-8 text-amber-600 mb-4" />
                  <h4 className="text-lg font-semibold text-slate-900 mb-2">{t(value.title)}</h4>
                  <p className="text-sm text-slate-600">{t(value.description)}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Team Section */}
          <div>
            <h3 className="text-2xl font-bold text-slate-900 mb-8 text-center">
              {language === 'en' ? 'Our Multidisciplinary Team' : 'Notre Équipe Multidisciplinaire'}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {team.map((member) => (
                <Card key={member.id} className="bg-white border-slate-200 hover:shadow-md transition-shadow">
                  <CardHeader>
                    <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center mb-4">
                      <IconComponent name={member.icon} className="w-6 h-6 text-amber-700" />
                    </div>
                    <CardTitle className="text-base text-slate-900">{t(member.title)}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-sm text-slate-600">{t(member.description)}</CardDescription>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section id="projects" className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              {language === 'en' ? 'Projects & Case Studies' : 'Projets & Études de Cas'}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {language === 'en'
                ? 'Our portfolio of successful industrial and mining projects'
                : 'Notre portefeuille de projets industriels et miniers réussis'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
            {projects.map((project) => (
              <Card key={project.id} className="border-slate-200 hover:shadow-lg transition-shadow">
                <CardHeader>
                  <Badge className="w-fit mb-2 bg-slate-100 text-slate-700 hover:bg-slate-200">
                    {t(project.category)}
                  </Badge>
                  <CardTitle className="text-slate-900">{t(project.title)}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-600">{t(project.description)}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Partnerships */}
          <div>
            <h3 className="text-2xl font-bold text-slate-900 mb-8 text-center">
              {language === 'en' ? 'Strategic Partnerships' : 'Partenariats Stratégiques'}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {partnerships.map((partner) => (
                <Card key={partner.id} className="bg-slate-50 border-slate-200">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-slate-900">{partner.name}</CardTitle>
                      <Badge variant="outline" className="border-amber-500 text-amber-700">
                        {language === 'en' ? 'Since' : 'Depuis'} {partner.year}
                      </Badge>
                    </div>
                    <CardDescription className="text-slate-600 font-medium">{partner.fullName}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-600 text-sm">{t(partner.description)}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Research Section */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              {language === 'en' ? 'Research & Development' : 'Recherche & Développement'}
            </h2>
            <p className="text-xl text-slate-300 mb-8">{t(researchStats.commitment)}</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-8 bg-slate-800 p-8 rounded-lg border border-slate-700">
              <div>
                <div className="text-4xl font-bold text-amber-500 mb-2">{researchStats.projects.number}</div>
                <div className="text-slate-300">{t(researchStats.projects.label)}</div>
              </div>
              <Separator orientation="vertical" className="h-16 hidden sm:block bg-slate-700" />
              <div className="text-slate-300">
                <p>{t(researchStats.projects.description)}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              {language === 'en' ? 'Contact Us' : 'Contactez-Nous'}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {language === 'en'
                ? 'Get in touch with our team for inquiries and consultations'
                : 'Contactez notre équipe pour des demandes de renseignements et consultations'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <Card className="bg-white border-slate-200">
              <CardHeader>
                <CardTitle className="text-slate-900">
                  {language === 'en' ? 'Contact Information' : 'Informations de Contact'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start space-x-3">
                  <Phone className="w-5 h-5 text-amber-600 mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-slate-900 mb-1">
                      {language === 'en' ? 'Phone Numbers' : 'Numéros de Téléphone'}
                    </p>
                    {contactInfo.phone.map((phone, index) => (
                      <p key={index} className="text-slate-600">{phone}</p>
                    ))}
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Mail className="w-5 h-5 text-amber-600 mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-slate-900 mb-1">
                      {language === 'en' ? 'Email' : 'Courriel'}
                    </p>
                    <p className="text-slate-600">{contactInfo.email}</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <MapPin className="w-5 h-5 text-amber-600 mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-slate-900 mb-1">
                      {language === 'en' ? 'Address' : 'Adresse'}
                    </p>
                    <p className="text-slate-600">{t(contactInfo.address)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white border-slate-200">
              <CardHeader>
                <CardTitle className="text-slate-900">
                  {language === 'en' ? 'Business Registration' : 'Enregistrement Commercial'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-sm font-medium text-slate-700 mb-1">RCCM</p>
                  <p className="text-slate-600">{contactInfo.registration.rccm}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-700 mb-1">ID.NAT</p>
                  <p className="text-slate-600">{contactInfo.registration.idnat}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-700 mb-1">
                    {language === 'en' ? 'Tax Number' : 'Numéro d\'Impôt'}
                  </p>
                  <p className="text-slate-600">{contactInfo.registration.tax}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12 border-t border-slate-800">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-amber-500 rounded flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-slate-900" />
                </div>
                <div>
                  <h3 className="text-lg font-bold">{companyInfo.name}</h3>
                  <p className="text-xs text-amber-400">{t(companyInfo.tagline)}</p>
                </div>
              </div>
              <p className="text-sm text-slate-400">
                {language === 'en'
                  ? 'Leading industrial and mining solutions in R.D. Congo'
                  : 'Solutions industrielles et minières de premier plan en R.D. Congo'}
              </p>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4">
                {language === 'en' ? 'Quick Links' : 'Liens Rapides'}
              </h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li>
                  <button onClick={() => scrollToSection('services')} className="hover:text-amber-400 transition-colors">
                    {language === 'en' ? 'Services' : 'Services'}
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('about')} className="hover:text-amber-400 transition-colors">
                    {language === 'en' ? 'About Us' : 'À Propos'}
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('projects')} className="hover:text-amber-400 transition-colors">
                    {language === 'en' ? 'Projects' : 'Projets'}
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('contact')} className="hover:text-amber-400 transition-colors">
                    {language === 'en' ? 'Contact' : 'Contact'}
                  </button>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4">
                {language === 'en' ? 'Contact' : 'Contact'}
              </h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li>{contactInfo.phone[0]}</li>
                <li>{contactInfo.email}</li>
                <li className="text-xs">{t(contactInfo.address)}</li>
              </ul>
            </div>
          </div>

          <Separator className="my-8 bg-slate-800" />

          <div className="text-center text-sm text-slate-400">
            <p>
              © {new Date().getFullYear()} {companyInfo.name}.{' '}
              {language === 'en' ? 'All rights reserved.' : 'Tous droits réservés.'}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;